import { bootstrapAuth, getAccessToken, refresh, getApiUrl } from '../../js/core/auth-client.js';

export type Permission = 'owner'|'read'|'edit';

function apiUrl(){
  return getApiUrl();
}

function buildHeaders(options: RequestInit = {}){
  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');
  const token = getAccessToken();
  if(token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function request(path:string, options:RequestInit={}, requireAuth=true, retry=true){
  if(requireAuth && !getAccessToken()){
    await bootstrapAuth();
  }

  if(requireAuth && !getAccessToken()){
    const error:any = new Error('Autenticación requerida. Inicia sesión para usar la colaboración.');
    error.status = 401;
    throw error;
  }

  const response = await fetch(`${apiUrl()}${path}`, {
    ...options,
    credentials: 'include',
    headers: buildHeaders(options),
  });

  let body:any = null;
  try { body = await response.json(); } catch(_) {}

  if(response.status === 401 && requireAuth && retry){
    const refreshed = await refresh(false);
    if(refreshed){
      return request(path, options, requireAuth, false);
    }
  }

  if(!response.ok){
    const error:any = new Error(body?.message || body?.error?.message || `Error HTTP ${response.status}`);
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return body;
}

export const collaborationApi={
  listCases:()=>request('/cases').then(b=>b.cases||[]),
  listCasesPage:(params:Record<string,string|number>={})=>request(`/cases${Object.keys(params).length ? `?${new URLSearchParams(Object.entries(params).map(([k,v])=>[k,String(v)]))}` : ''}`),
  members:(caseId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}`),
  share:(caseId:string,email:string,permission:'read'|'edit')=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/share`,{method:'POST',body:JSON.stringify({email,permission})}),
  permission:(caseId:string,userId:string,permission:'read'|'edit')=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/members/${encodeURIComponent(userId)}`,{method:'PATCH',body:JSON.stringify({permission})}),
  remove:(caseId:string,userId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/members/${encodeURIComponent(userId)}`,{method:'DELETE'}),
  outgoingInvitations:(caseId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/invitations`),
  revokeInvitation:(caseId:string,invitationId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/invitations/${encodeURIComponent(invitationId)}`,{method:'DELETE'}),
  transferOwnership:(caseId:string,userId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/transfer-ownership`,{method:'POST',body:JSON.stringify({userId})}),
  invitations:()=>request('/collaboration/invitations'),
  previewInvitation:(id:string)=>request(`/invitations/${encodeURIComponent(id)}`,{},false),
  acceptInvitation:(id:string)=>request(`/collaboration/invitations/${encodeURIComponent(id)}/accept`,{method:'POST'}),
  presence:(caseId:string,activity='viewing',displayName?:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/presence`,{method:'POST',body:JSON.stringify({activity,displayName})}),
  getPresence:(caseId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/presence`),
  leave:(caseId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/presence`,{method:'DELETE'}),
  activity:(caseId:string)=>request(`/collaboration/cases/${encodeURIComponent(caseId)}/activity`),
  comments:(caseId:string,includeResolved=true)=>request(`/cases/${encodeURIComponent(caseId)}/comments?includeResolved=${includeResolved}`),
  createComment:(caseId:string,body:{body:string;targetType?:'case'|'card'|'connection';targetId?:string;parentId?:string;mentions?:string[]})=>request(`/cases/${encodeURIComponent(caseId)}/comments`,{method:'POST',body:JSON.stringify(body)}),
  updateComment:(caseId:string,commentId:string,body:string,mentions?:string[])=>request(`/cases/${encodeURIComponent(caseId)}/comments/${encodeURIComponent(commentId)}`,{method:'PATCH',body:JSON.stringify({body,mentions})}),
  resolveComment:(caseId:string,commentId:string,resolved=true)=>request(`/cases/${encodeURIComponent(caseId)}/comments/${encodeURIComponent(commentId)}/resolve`,{method:'POST',body:JSON.stringify({resolved})}),
  commentHistory:(caseId:string,commentId:string)=>request(`/cases/${encodeURIComponent(caseId)}/comments/${encodeURIComponent(commentId)}/history`)
  ,notifications:(params:Record<string,string|number|boolean>={})=>{
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== '').map(([key,value]) => [key,String(value)]));
    return request(`/notifications${query.toString() ? `?${query}` : ''}`);
  },
  markNotificationRead:(id:string)=>request(`/notifications/${encodeURIComponent(id)}/read`,{method:'POST'}),
  markAllNotificationsRead:()=>request('/notifications/read-all',{method:'POST'}),
  notificationPreferences:()=>request('/notifications/preferences'),
  updateNotificationPreferences:(preferences:Record<string,boolean>)=>request('/notifications/preferences',{method:'PATCH',body:JSON.stringify(preferences)})
  ,admin:{
    status:()=>request('/admin/status'),
    users:(params:Record<string,string|number>={})=>request(`/admin/users${Object.keys(params).length ? `?${new URLSearchParams(Object.entries(params).map(([k,v])=>[k,String(v)]))}` : ''}`),
    setRole:(id:string,role:'user'|'admin')=>request(`/admin/users/${encodeURIComponent(id)}/role`,{method:'PATCH',body:JSON.stringify({role})}),
    cases:(params:Record<string,string|number>={})=>request(`/admin/cases${Object.keys(params).length ? `?${new URLSearchParams(Object.entries(params).map(([k,v])=>[k,String(v)]))}` : ''}`),
    deleteCase:(id:string)=>request(`/admin/cases/${encodeURIComponent(id)}`,{method:'DELETE'}),
    permission:(caseId:string,userId:string,permission:'read'|'edit')=>request(`/admin/cases/${encodeURIComponent(caseId)}/permissions/${encodeURIComponent(userId)}`,{method:'PATCH',body:JSON.stringify({permission})}),
    activity:(params:Record<string,string|number>={})=>request(`/admin/activity${Object.keys(params).length ? `?${new URLSearchParams(Object.entries(params).map(([k,v])=>[k,String(v)]))}` : ''}`),
    cleanup:()=>request('/admin/maintenance/cleanup',{method:'POST'}),
    exportData:()=>request('/admin/export')
  },
  privacy:{
    export:()=>request('/privacy/export'),
    accessLog:(limit=100)=>request(`/privacy/access-log?limit=${limit}`),
    deleteAccount:(confirmation:string)=>request('/privacy/account',{method:'DELETE',body:JSON.stringify({confirmation})})
  }
};
